import { useMsal } from "@azure/msal-react";
import { useRef, useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import readNDJSONStream from "ndjson-readablestream";
import { v4 as uuidv4 } from "uuid";
import { setTimeout } from "worker-timers";
import {
    Checkbox, Panel, DefaultButton, SpinButton, PanelType, IDropdownOption, Dropdown, Spinner,
    SpinnerSize, PrimaryButton, Dialog, TextField, DialogFooter, DialogType, Stack, StackItem, Label,
    SearchBox, SelectableOptionMenuItemType, IComboBoxOption, Link, IconButton, Toggle, ITag
} from "@fluentui/react";

import logo from "../../assets/coeus-large.png";
import styles from "./Chat.module.css";

import {
    chatApi, RetrievalMode, ChatAppResponse, ChatAppRequest, ChatTurn, GptDeployment, getAllFolders,
    getGptDeployments, setGptDeployment, getInfoData, GetInfoResponse, PromptTemplate, getPromptTemplates,
    upsertPromptTemplate, stopStream, UserData, getConversation, getConversationHistory,
    ConversationHistory, updateConversation, ChatAppRequestOverrides, submitResponseFeedback
} from "../../api";
import { usePersistentState } from "../../hooks/usePersistentState"
import { getToken } from "../../authConfig";
import { handleError, handleSuccess } from "../../toaster";
import { UserContext } from "../../components/UserContext";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { ResponseLengthSlider } from "../../components/ResponseLengthSlider";
import { ResponseTempSlider } from "../../components/ResponseTempSlider";
import { Tooltips } from "../../components/Tooltips"
import { PromptOverride } from "../../components/PromptOverride";
import { TopPSlider } from "../../components/TopPSlider";
import { FolderPicker } from "../../components/FolderPicker";
import { TagPickerInline } from "../../components/TagPicker";
import { ModelPicker } from "../../components/ModelPicker";
import { PromptTemplatePicker } from "../../components/PromptTemplatePicker";
import { ChatHistoryPanel } from "../../components/ChatHistoryPanel"
import { ChatHistoryButton } from "../../components/ChatHistoryButton";

const Chat = () => {
    const client = useMsal().instance;
    const navigate = useNavigate();

    const userContext = useContext(UserContext);
    const userData = userContext.userData as UserData;

    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = useState(false);
    const [isChatHistoryPanelOpen, setIsChatHistoryPanelOpen] = useState(false);
    const [isTemplateSaveDialogOpen, setIsTemplateSaveDialogOpen] = useState(false);

    const [allGptDeployments, setAllGptDeployments] = useState<GptDeployment[]>([]);
    const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
    const [isAdvancedModeEnabled, setIsAdvancedModeEnabled] = usePersistentState("isAdvancedModeEnabled", false);
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [defaultGptDeployment, setDefaultGptDeployment] = useState<string>("");
    const [selectedGptDeployment, setSelectedGptDeployment] = usePersistentState<string | undefined>("selectedGptDeployment", undefined);
    const [promptOverride, setPromptOverride] = usePersistentState<string>("promptOverride", "");
    const [retrieveCount, setRetrieveCount] = usePersistentState<number>("retrieveCount", 10);
    const [retrievalMode, setRetrievalMode] = usePersistentState<RetrievalMode>("retrievalMode", RetrievalMode.None);
    const [suggestFollowupQuestions, setSuggestFollowupQuestions] = usePersistentState<boolean>("suggestFollowupQuestions", true);
    const [responseLength, setResponseLength] = usePersistentState<number>("responseLength", 2048);
    const [responseTemp, setResponseTemp] = usePersistentState<number>("responseTemp", 0.4);
    const [topP, setTopP] = usePersistentState<number>("topP", 1.0);
    const [selectableFolders, setSelectableFolders] = useState<IComboBoxOption[]>([]);
    const [selectedFolders, setSelectedFolders] = usePersistentState<string[]>("selectedFolders", []);
    const [selectedTags, setSelectedTags] = usePersistentState<ITag[]>("selectedTags", []);
    const [selectedPromptTemplate, setSelectedPromptTemplate] = usePersistentState<PromptTemplate | null>("selectedPromptTemplate", null);

    const { conversationId: paramConversationId } = useParams();
    const [conversationHistoryUserId, setConversationHistoryUserId] = useState<string>("");
    const [chatHistory, setChatHistory] = useState<ConversationHistory | undefined>(undefined);
    const [conversationState, setConversationState] = useState(() => {
        const savedConversationId = sessionStorage.getItem("activeConversationId");
        const initialId = paramConversationId || savedConversationId || uuidv4();
        return {
            id: initialId,
            isNew: !paramConversationId && !savedConversationId,
            hasInteracted: !!savedConversationId,
        };
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [chatError, setChatError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeCitationSourceFile, setActiveCitationSourceFile] = useState<string>();
    const [activeCitationSourceFilePageNumber, setActiveCitationSourceFilePageNumber] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [abortController, setAbortController] = useState<AbortController | null>(null);

    const [templateSaveName, setTemplateSaveName] = useState("");
    const [isTemplateSaving, setIsTemplateSaving] = useState(false);

    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [showScrollDownIcon, setShowScrollDownIcon] = useState(false);
    const [showScrollUpIcon, setShowScrollUpIcon] = useState(false);

    const [activeAudioAnswer, setActiveAudioAnswer] = useState<number | null>(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false);
    const [cachedAudioUrls, setCachedAudioUrls] = useState<{ [key: number]: string }>({});
    const ttsAudio = useRef(new Audio()).current;

    const onToggleAudioPlay = (index: number, speechUrl: string) => {
        if (isAudioPlaying && activeAudioAnswer === index) {
            stopAudio();  // Stop if it's currently playing the same answer
        } else {
            if (isAudioPlaying) {
                stopAudio();  // Stop any currently playing audio
            }
            playAudio(index, speechUrl);
        }
    };

    const playAudio = (index: number, speechUrl: string) => {
        ttsAudio.src = speechUrl;
        ttsAudio.play().then(() => {
            setActiveAudioAnswer(index);
            setIsAudioPlaying(true);
        }).catch((error) => {
            handleError(error, "Error playing audio:");
        });

        ttsAudio.onended = () => {
            setIsAudioPlaying(false);
            setActiveAudioAnswer(null);
        };
    };

    const stopAudio = () => {
        ttsAudio.pause();
        ttsAudio.currentTime = 0;
        setActiveAudioAnswer(null);
        setIsAudioPlaying(false);
    };

    const cacheAudioUrl = (index: number, url: string) => {
        setCachedAudioUrls(prevUrls => ({
            ...prevUrls,
            [index]: url
        }));
    };

    const handleAsyncRequest = async (
        question: string,
        responseBody: ReadableStream<any>,
        signal: AbortSignal
    ) => {
        let answer: string = "";
        let chatResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...chatResponse,
                        message: answer
                    };
                    setAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 10);
            });
        };

        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (signal.aborted) {
                    // Abort the stream if requested
                    break;
                }
                if (event["context"]) {
                    chatResponse.context = { ...chatResponse.context, ...event["context"] };
                } else if (event["content"]) {
                    await updateState(event["content"]);
                    setIsLoading(false);
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } catch (e) {
            if (!signal.aborted) {
                // Non-abort-related error
                throw e;
            }
        }
        finally {
            setIsStreaming(false);
        };

        const fullResponse: ChatAppResponse = {
            ...chatResponse,
            message: answer
        };
        return fullResponse;
    };

    const makeApiRequest = async (question: string) => {
        const controller = new AbortController();
        setAbortController(controller);
        lastQuestionRef.current = question;

        chatError && setChatError(undefined);
        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        scrollToBottom();

        try {
            setConversationState((prevState) => {
                if (!prevState.hasInteracted) {
                    // Save to sessionStorage as user has begun interacting with this conversation
                    sessionStorage.setItem("activeConversationId", prevState.id);
                    return { ...prevState, hasInteracted: true };
                }
                return prevState;
            });

            if (window.location.hash !== `#/Chat/${conversationState.id}`) {
                navigate(`/Chat/${conversationState.id}`, { replace: true });
            };

            const messages: ChatTurn[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].message, role: "assistant" }
            ]);

            const request: ChatAppRequest = {
                conversation_id: conversationState.id,
                messages: [...messages, { content: question, role: "user" }],
                context: {
                    overrides: {
                        retrieval_mode: retrievalMode,
                        top: retrieveCount,
                        temperature: responseTemp,
                        prompt_template: promptOverride.length === 0 ? undefined : promptOverride,
                        suggest_followup_questions: suggestFollowupQuestions,
                        response_length: responseLength,
                        top_p: topP,
                        // If no folders selected, or selectAll is selected
                        // send "all" to prevent unnecessary filtering
                        // unless user is not admin then use filtered list of folders
                        selected_folders: retrievalMode === RetrievalMode.None ? ""
                            : (selectedFolders.includes("selectAll") || selectedFolders.length === 0)
                                ? (userData.is_admin
                                    ? "All"
                                    : selectedFolders.filter(f => f !== "selectAll").join(","))
                                : selectedFolders.join(","),
                        selected_tags: retrievalMode === RetrievalMode.None ? ""
                            : selectedTags.map(tag => tag.name).join(",")
                    }
                }
            };

            const token = client ? await getToken(client) : undefined;
            const response = await chatApi(token, request, controller.signal);
            if (!response.body) {
                throw Error("No response body");
            }
            const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, response.body, controller.signal);
            setAnswers([...answers, [question, parsedResponse]]);
        } catch (e) {
            setChatError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = async (startNew = true) => {
        const token = client ? await getToken(client) : undefined;
        lastQuestionRef.current = "";
        chatError && setChatError(undefined);
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        stopAudio();
        setCachedAudioUrls([]);
        setIsLoading(false);
        setShowScrollDownIcon(false);
        setShowScrollUpIcon(false);
        if (isStreaming) { stopStream(token) };
        setIsStreaming(false);
        sessionStorage.removeItem("activeConversationId");
        if (startNew) {
            // Generate and set a new conversation ID
            setConversationState({
                id: uuidv4(),
                isNew: true,
                hasInteracted: false,
            });
            navigate("/Chat", { replace: true });
        }
    };

    const onPromptOverrideChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptOverride(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "5"));
    };

    const onRetrievalModeChange = (mode: RetrievalMode) => {
        setRetrievalMode(mode);
        if (RetrievalMode.None !== mode) {
            setSelectedFolders(["selectAll", ...selectableFolders.map(o => o.key as string)]);
        }
    };

    const onSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setSuggestFollowupQuestions(!!checked);
    };

    const onShowCitation = (citation: string, citationSourceFile: string, citationSourceFilePageNumber: string, index: number) => {
        setActiveCitation(citation);
        setActiveCitationSourceFile(citationSourceFile);
        setActiveCitationSourceFilePageNumber(citationSourceFilePageNumber);
        setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        setSelectedAnswer(index);
        setIsAnalysisPanelOpen(true);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        setActiveAnalysisPanelTab(tab);
        setSelectedAnswer(index);
        setIsAnalysisPanelOpen(true);
    };

    const onSelectedKeyChanged = (selectedFolders: string[]) => {
        setSelectedFolders(selectedFolders);
    };

    const onSelectedTagsChange = (selectedTags: ITag[]) => {
        setSelectedTags(selectedTags)
    }

    const onAnalysisPanelClose = () => {
        setIsAnalysisPanelOpen(false);
        setActiveAnalysisPanelTab(undefined);
    };

    const onConfigPanelOpen = async () => {
        setIsConfigPanelOpen(true);
        await fetchPromptTemplates();
    };

    const fetchPromptTemplates = async () => {
        const token = client ? await getToken(client) : undefined;
        const templates: PromptTemplate[] = await getPromptTemplates(token);
        setPromptTemplates(templates);
    };

    const onChatHistoryOpen = async () => {
        const token = client ? await getToken(client) : undefined;
        setIsChatHistoryPanelOpen(true);
        const chatHistory: ConversationHistory = await getConversationHistory(token, userData.userPrincipalName)
        setChatHistory(chatHistory);
    };

    const onChatHistorySearch = async (user_name: string) => {
        const token = client ? await getToken(client) : undefined;
        setConversationHistoryUserId(user_name);
        const chatHistory: ConversationHistory = await getConversationHistory(token, user_name)
        setChatHistory(chatHistory);
    };

    const onConversationUpdated = async (conversation_id: string, new_name?: string, archived?: boolean) => {
        try {
            if (archived || new_name) {
                // Update conversation details locally
                setChatHistory(history => {
                    if (history) {
                        let updatedHistory = history.history;

                        // If the conversation is being archived, filter it out
                        if (archived) {
                            updatedHistory = updatedHistory.filter(conversation => conversation.conversation_id !== conversation_id);
                        }
                        // Otherwise, if a new name is provided, update the conversation name
                        else if (new_name) {
                            updatedHistory = updatedHistory.map(conversation => {
                                if (conversation.conversation_id === conversation_id) {
                                    // Found the conversation that was renamed, update its name
                                    return { ...conversation, conversation_name: new_name };
                                }
                                return conversation; // Return all other conversations unchanged
                            });
                        }

                        // Return new state with updated or filtered history
                        return { ...history, history: updatedHistory };
                    };
                    return history; // In case there's no update needed, return the original history
                });
            }

            // Update in Cosmos
            const token = client ? await getToken(client) : undefined;
            await updateConversation(
                token,
                conversationHistoryUserId || userData.userPrincipalName,
                conversation_id,
                new_name,
                archived);
        } catch (error) {
            handleError(error, "An error occurred updating the conversation");
        }
    };

    const onFeedbackSubmitted = async (conversation_id: string, request_id: string, sentiment: string, comments?: string) => {
        try {
            // Update in Cosmos
            const token = client ? await getToken(client) : undefined;
            await submitResponseFeedback(
                token,
                userData.userPrincipalName,
                conversation_id,
                request_id,
                sentiment,
                comments
            );
        } catch (error) {
            handleError(error, "An error occurred while submitting feedback");
        }
    };

    const onConversationClicked = async (conversation_id: string) => {
        clearChat(false); // Don't start new conversation
        setConversationState({ id: conversation_id, isNew: false, hasInteracted: false });
        setIsChatHistoryPanelOpen(false);
        navigate(`/Chat/${conversation_id}`);
        fetchConversation(conversation_id);
    };

    const fetchConversation = async (conversation_id: string) => {
        try {
            error && setError(undefined);
            lastQuestionRef.current = "Loading Conversation from History..."
            setIsLoadingHistory(true);

            const token = client ? await getToken(client) : undefined;
            const data = await getConversation(token, conversationHistoryUserId || userData.userPrincipalName, conversation_id);

            // Set Gpt Deployment
            if (data.gpt_deployment) {
                onGptDeploymentChange(data.gpt_deployment);
            };

            // Set overrides
            if (data.overrides) {
                setOverrides(data.overrides)
            };

            // Set answers/conversation
            if (data.history) {
                const formattedHistory: [string, ChatAppResponse][] = data.history.map(item => [item.user, item.response]);
                setAnswers(formattedHistory);
                const mostRecentQuestion = formattedHistory[formattedHistory.length - 1][0];
                lastQuestionRef.current = mostRecentQuestion; // Update the ref to the most recent question
            };
        } catch (error) {
            lastQuestionRef.current = "An error occurred loading Conversation from History"
            setError(error);
        }
        finally {
            setIsLoadingHistory(false);
            scrollToBottom();
        }
    };

    const setOverrides = (overrides: ChatAppRequestOverrides) => {
        setSuggestFollowupQuestions(overrides.suggest_followup_questions || suggestFollowupQuestions);
        setResponseLength(overrides.response_length || responseLength);
        setResponseTemp(overrides.temperature || responseTemp);
        setSuggestFollowupQuestions(overrides.suggest_followup_questions || suggestFollowupQuestions);
        setRetrieveCount(overrides.top || retrieveCount);
        setTopP(overrides.top_p || topP);
        setPromptOverride(overrides.prompt_template || "");
        setRetrievalMode(overrides.retrieval_mode || retrievalMode);
        if (overrides.selected_folders) {
            setSelectedFolders(
                overrides.selected_folders === "All"
                    ? ["selectAll"] // Set the state to ["selectAll"]
                    : (overrides.selected_folders || "").split(",").filter(Boolean) // Otherwise, split and filter
            );
        };
        if (overrides.selected_tags) {
            const tagsArray: ITag[] = overrides.selected_tags.split(",").map((name, index) => ({
                key: `key${index}`,
                name: name
            }));
            setSelectedTags(tagsArray);
        };
    };

    const onStopClick = async () => {
        try {
            if (abortController) {
                abortController.abort();
            }
            const token = client ? await getToken(client) : undefined;
            return stopStream(token);
        } catch (error) {
            handleError(error, "An error occurred trying to stop the stream");
        }
    };

    const onGptDeploymentChange = async (deploymentName: string) => {
        const deploymentToUpdate = allGptDeployments.find(d => d.deploymentName === deploymentName);

        if (deploymentToUpdate && selectedGptDeployment !== deploymentName) {
            const token = client ? await getToken(client) : undefined;
            setSelectedGptDeployment(deploymentName);
            setGptDeployment(token, deploymentToUpdate);
        }
    };

    const onPromptTemplatePickerChange = async (template: PromptTemplate) => {
        if (template) {
            const overrides: ChatAppRequestOverrides = {
                response_length: template.response_length,
                temperature: template.temperature,
                top_p: template.top_p,
                top: template.top,
                prompt_template: template.prompt_override,
                suggest_followup_questions: template.suggest_followup_questions,
                retrieval_mode: template.retrieval_mode,
                selected_folders: template.selected_folders,
                selected_tags: template.selected_tags,
            };
            setOverrides(overrides);
            setSelectedPromptTemplate(template);
            await onGptDeploymentChange(template.deployment_name);
        }
    };

    const onResetConfigButtonClicked = async () => {
        const overrides: ChatAppRequestOverrides = {
            response_length: 2048,
            temperature: 0.4,
            top_p: 1,
            top: 10,
            prompt_template: "",
            suggest_followup_questions: true,
            retrieval_mode: RetrievalMode.None
        };
        setOverrides(overrides);
        setSelectedTags([]);
        setSelectedFolders(["selectAll", ...selectableFolders.map(o => o.key as string)])
        setSelectedPromptTemplate(null);
        await onGptDeploymentChange(defaultGptDeployment || "Unknown");
    };

    const onAdvancedModeToggle = async () => {
        if (isAdvancedModeEnabled) {
            // Reset settings to default if switching 
            // from advanced back to simple mode
            const overrides: ChatAppRequestOverrides = {
                response_length: 2048,
                temperature: 0.4,
                top_p: 1,
                top: 10,
                prompt_template: "",
                suggest_followup_questions: true,
            };
            setOverrides(overrides);
            setSelectedPromptTemplate(null);
            await onGptDeploymentChange(defaultGptDeployment || "Unknown");
        }
        setIsAdvancedModeEnabled(!isAdvancedModeEnabled);
    };

    const onSaveConfigButtonClicked = () => {
        setTemplateSaveName(selectedPromptTemplate ? selectedPromptTemplate.display_name : "");
        setIsTemplateSaveDialogOpen(true);
    };

    const onSavePromptTemplate = async () => {
        setIsTemplateSaving(true);

        const promptTemplate: PromptTemplate = {
            id: selectedPromptTemplate?.id || "",
            user_id: userData.userPrincipalName,
            display_name: templateSaveName,
            deployment_name: selectedGptDeployment || defaultGptDeployment,
            prompt_override: promptOverride,
            response_length: responseLength,
            temperature: responseTemp,
            suggest_followup_questions: suggestFollowupQuestions,
            top: retrieveCount,
            top_p: topP,
            retrieval_mode: retrievalMode,
            // If no folders selected, or selectAll is selected
            // send "all" to prevent unnecessary filtering
            // unless user is not admin then use filtered list of folders
            selected_folders: (selectedFolders.includes("selectAll") || selectedFolders.length === 0)
                ? (userData.is_admin
                    ? "All"
                    : selectedFolders.filter(f => f !== "selectAll").join(","))
                : selectedFolders.join(","),
            selected_tags: selectedTags.map(tag => tag.name).join(",")
        };
        try {
            const token = client ? await getToken(client) : undefined;
            await upsertPromptTemplate(token, promptTemplate);
            handleSuccess("Prompt Template saved successfully!");
            setIsTemplateSaveDialogOpen(false); // Close the dialog after success
        } catch (error) {
            handleError(error, "Failed to save the template. Please try again");
        } finally {
            await fetchPromptTemplates();
            setIsTemplateSaving(false); // End saving whether it was successful or not
        }
    };

    async function fetchFoldersFromCosmos() {
        try {
            const token = client ? await getToken(client) : undefined;
            const response = await getAllFolders(token);
            const folders = response.folders;

            let newOptions: IComboBoxOption[] = [
                { key: "selectAll", text: "Select All", itemType: SelectableOptionMenuItemType.SelectAll }
            ];

            const folderOptions: IComboBoxOption[] = [];
            folders.forEach(folderName => {
                const trimmedFolderName = folderName.trim();
                const textValue = trimmedFolderName === userData.userPrincipalName ? "My Data" : trimmedFolderName;
                folderOptions.push({ key: trimmedFolderName, text: textValue });
            });

            // Check if the user folder exists in folderOptions
            // If it doesn't exist, add it to folderOptions
            const userFolderExists = folderOptions.some(option => option.key === userData.userPrincipalName);
            if (!userFolderExists) {
                folderOptions.push({ key: userData.userPrincipalName, text: "My Data" });
            }

            // Sort folderOptions
            folderOptions.sort((a, b) => a.text.localeCompare(b.text));

            // Combine the sorted folders with the headers
            newOptions = [...newOptions, ...folderOptions];

            setSelectableFolders(newOptions);
            // Set all folders selected by default
            setSelectedFolders(["selectAll", ...folderOptions.map(o => o.key as string)])
        } catch (error) {
            handleError(error, "Error while fetching folders from API")
        }
    };

    const handleRagClick = () => {
        if (retrievalMode === RetrievalMode.None) {
            // If currently "none", switch to "hybrid" upon click
            onRetrievalModeChange(RetrievalMode.Hybrid);
        } else {
            // If currently anything other than "none", switch to "none" upon click
            onRetrievalModeChange(RetrievalMode.None);
        }
    };

    const handleScroll = () => {
        const element = chatMessageStreamEnd.current;
        if (element) {
            const atBottom = element.getBoundingClientRect().bottom <= window.innerHeight;
            const atTop = window.scrollY === 0;

            setIsUserScrolling(!atBottom);
            setShowScrollDownIcon(!atBottom);
            setShowScrollUpIcon(!atTop);
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        if (!isUserScrolling) {
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [isLoading]);

    useEffect(() => {
        if (!isUserScrolling) {
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
        }
    }, [answers]);

    const scrollToBottom = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
        setIsUserScrolling(false);
        setShowScrollDownIcon(false);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setShowScrollUpIcon(false);
    };

    useEffect(() => {
        async function init() {
            const token = client ? await getToken(client) : undefined;
            getInfoData(token)
                .then((response: GetInfoResponse) => {
                    setDefaultGptDeployment(response.AZURE_OPENAI_CHATGPT_DEPLOYMENT);
                    setSelectedGptDeployment(response.AZURE_OPENAI_CHATGPT_DEPLOYMENT);
                })
                .catch(err => handleError(err, "Failed to fetch InfoData"));

            getGptDeployments(token)
                .then(setAllGptDeployments)
                .catch(err => handleError(err, "Failed to fetch GPT Deployments"));

            await fetchFoldersFromCosmos();
        }
        init();
    }, []);

    useEffect(() => {
        const handleInitialLoad = async () => {
            if (!conversationState.isNew) {
                if (window.location.hash !== `#/Chat/${conversationState.id}`) {
                    navigate(`/Chat/${conversationState.id}`, { replace: true });
                }
                await fetchConversation(conversationState.id);
            }
        };
        handleInitialLoad();
    }, []);

    const retrievalModeOptions: IDropdownOption[] = [
        { key: "hybrid", text: "Vectors + Text (Hybrid)", selected: retrievalMode == RetrievalMode.Hybrid, data: RetrievalMode.Hybrid },
        { key: "vectors", text: "Vectors", selected: retrievalMode == RetrievalMode.Vectors, data: RetrievalMode.Vectors },
        { key: "text", text: "Text", selected: retrievalMode == RetrievalMode.Text, data: RetrievalMode.Text },
        { key: "none", text: "No Document Search (Chat Direct)", selected: retrievalMode == RetrievalMode.None, data: RetrievalMode.None }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.commandsContainer}>
                <ClearChatButton
                    className={styles.commandButton}
                    onClick={clearChat}
                    disabled={!lastQuestionRef.current || isLoading || isLoadingHistory || isStreaming}
                />
                <ChatHistoryButton
                    className={styles.commandButton}
                    onClick={onChatHistoryOpen}
                    disabled={isLoading || isLoadingHistory || isStreaming}
                />
                <SettingsButton
                    className={styles.commandButton}
                    onClick={onConfigPanelOpen}
                    disabled={isLoading || isLoadingHistory || isStreaming}
                />
            </div>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            <img
                                src={logo}
                                className={`${styles.chatLogo} ${userContext.useDarkMode ? styles.chatLogoDark : ""}`}
                                title="What is Coeus?"
                                onClick={() => makeApiRequest("What is Coeus?")} />
                            <h2 className={styles.chatEmptyStateSubtitle}>Ask anything or choose a Prompt Template in <Link onClick={() => onConfigPanelOpen()}> Settings</Link></h2>
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {answers.map((answer, index) => (
                                <div key={index}>
                                    <UserChatMessage
                                        message={answer[0]}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                    {answer[1]?.context?.error ? (
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerError
                                                error={answer[1].context.error}
                                                useDarkMode={userContext.useDarkMode}
                                            />
                                        </div>
                                    ) : (
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                key={index}
                                                index={index}
                                                conversationId={conversationState.id}
                                                isStreaming={false}
                                                question={answer[0]}
                                                answer={answer[1]}
                                                isAudioPlaying={index === activeAudioAnswer && isAudioPlaying}
                                                onToggleAudioPlay={onToggleAudioPlay}
                                                cachedAudioUrl={cachedAudioUrls[index] || null}
                                                onCacheAudioUrl={cacheAudioUrl}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={(c, s, p) => onShowCitation(c, s, p, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={suggestFollowupQuestions && answers.length - 1 === index}
                                                onSettingsClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)}
                                                onRegenerateClick={() => makeApiRequest(answers[index][0])}
                                                onFeedbackSubmitted={(c, r, s, f) => onFeedbackSubmitted(c, r, s, f)}
                                                useDarkMode={userContext.useDarkMode}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage
                                        message={lastQuestionRef.current}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading useDarkMode={userContext.useDarkMode} />
                                    </div>
                                </>
                            )}
                            {isLoadingHistory && (
                                <div className={styles.chatEmptyState}>
                                    <Spinner size={SpinnerSize.large} label={lastQuestionRef.current} ariaLive="assertive" />
                                </div>
                            )}
                            {error ? (
                                <div className={styles.chatEmptyState}>
                                    <h2>{lastQuestionRef.current}</h2>
                                    {error.toString()}
                                    <div style={{ marginTop: 10 }}>
                                        <PrimaryButton onClick={() => clearChat()} text="Start New Conversation" />
                                    </div>
                                </div>
                            ) : null}
                            {chatError ? (
                                <>
                                    <UserChatMessage
                                        message={lastQuestionRef.current}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError
                                            error={chatError.toString()}
                                            onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                            useDarkMode={userContext.useDarkMode}
                                        />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                    <div className={styles.chatInput}>
                        {showScrollUpIcon && (
                            <IconButton
                                iconProps={{ iconName: "ChevronUp" }}
                                onClick={scrollToTop}
                                className={`${styles.scrollButton} ${styles.scrollButtonUp}`}
                            />
                        )}
                        {showScrollDownIcon && (
                            <IconButton
                                iconProps={{ iconName: "ChevronDown" }}
                                onClick={scrollToBottom}
                                className={`${styles.scrollButton} ${styles.scrollButtonDown}`}
                            />
                        )}
                        <QuestionInput
                            clearOnSend
                            disabled={isLoading || isLoadingHistory || error != null}
                            onSend={question => makeApiRequest(question)}
                            clearChatDisabled={!lastQuestionRef.current || isLoading || isStreaming}
                            onClearClick={clearChat}
                            onStopClick={onStopClick}
                            isStreaming={isStreaming}
                            useDarkMode={userContext.useDarkMode}
                            onRetrievalModeChange={(m) => onRetrievalModeChange(m)}
                            retrievalMode={retrievalMode}
                        />
                    </div>
                </div>
                {answers.length > 0 && answers[selectedAnswer]?.length > 0 && activeAnalysisPanelTab && (
                    <Panel
                        type={PanelType.large}
                        isOpen={isAnalysisPanelOpen}
                        isBlocking={true}
                        onDismiss={() => onAnalysisPanelClose()}
                        closeButtonAriaLabel="Close"
                        onRenderFooterContent={() =>
                            <PrimaryButton
                                iconProps={{ iconName: "Cancel" }}
                                className={styles.panelButton}
                                onClick={() => onAnalysisPanelClose()}>
                                Close
                            </PrimaryButton>}
                        isFooterAtBottom={true}>
                        <AnalysisPanel
                            className={styles.chatAnalysisPanel}
                            activeCitation={activeCitation}
                            sourceFile={activeCitationSourceFile}
                            pageNumber={activeCitationSourceFilePageNumber}
                            onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                            answer={answers[selectedAnswer][1]}
                            activeTab={activeAnalysisPanelTab}
                            useDarkMode={userContext.useDarkMode}
                        />
                    </Panel>
                )}
                <Panel
                    type={PanelType.custom}
                    customWidth="450px"
                    headerText="Chat History"
                    isOpen={isChatHistoryPanelOpen}
                    isBlocking={true}
                    onDismiss={() => setIsChatHistoryPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    onRenderFooterContent={() =>
                        <PrimaryButton
                            className={styles.panelButton}
                            onClick={() => setIsChatHistoryPanelOpen(false)}
                            text="Close"
                        />}
                    isFooterAtBottom={true}>
                    {userData.is_admin &&
                        <SearchBox
                            placeholder="Search by User (user.name@example.com)"
                            onSearch={newValue => onChatHistorySearch(newValue)}
                        />
                    }
                    <ChatHistoryPanel
                        className={styles.chatSettingsSeparator}
                        chatHistory={chatHistory}
                        onConversationClicked={c => onConversationClicked(c)}
                        onConversationUpdated={(c, n, a) => onConversationUpdated(c, n, a)}
                    />
                </Panel>
                <Panel
                    type={PanelType.medium}
                    headerText="Adjust Settings"
                    isOpen={isConfigPanelOpen}
                    isBlocking={true}
                    hasCloseButton={false}
                    onRenderFooterContent={() =>
                        <div className={styles.panelFooter}>
                            <PrimaryButton
                                iconProps={{ iconName: "Cancel" }}
                                className={styles.panelButton}
                                disabled={retrievalMode !== RetrievalMode.None && selectedFolders.length === 0}
                                onClick={() => setIsConfigPanelOpen(false)}
                                text="Close"
                            />
                            <DefaultButton
                                iconProps={{ iconName: "Refresh" }}
                                className={styles.panelButton}
                                onClick={() => onResetConfigButtonClicked()}
                                text="Reset"
                            />
                            <DefaultButton
                                iconProps={{ iconName: "Save" }}
                                className={styles.panelButton}
                                onClick={() => onSaveConfigButtonClicked()}
                                text="Save"
                            />
                            <Toggle
                                label="Advanced"
                                inlineLabel
                                onText="On"
                                offText="Off"
                                checked={isAdvancedModeEnabled}
                                onChange={onAdvancedModeToggle}
                            />
                        </div>
                    }
                    isFooterAtBottom={true}>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <StackItem grow align="start" styles={{ root: { width: "50%" } }}>
                            <Label className={styles.panelSubHeaderText}>Model Settings</Label>
                            <PromptTemplatePicker
                                className={styles.chatSettingsSeparator}
                                promptTemplates={promptTemplates}
                                selectedTemplate={selectedPromptTemplate}
                                onChange={onPromptTemplatePickerChange}
                                userData={userData}
                            />
                            {selectedGptDeployment && (
                                <ModelPicker
                                    className={styles.chatSettingsSeparator}
                                    deployments={allGptDeployments}
                                    selectedGptDeployment={selectedGptDeployment}
                                    onGptDeploymentChange={onGptDeploymentChange}
                                />
                            )}
                            {isAdvancedModeEnabled && (
                                <>
                                    <ResponseLengthSlider
                                        className={styles.chatSettingsSeparator}
                                        onChange={setResponseLength}
                                        value={responseLength}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                    <ResponseTempSlider
                                        className={styles.chatSettingsSeparator}
                                        onChange={setResponseTemp}
                                        value={responseTemp}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                    <TopPSlider
                                        className={styles.chatSettingsSeparator}
                                        onChange={setTopP}
                                        value={topP}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                </>
                            )}
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={suggestFollowupQuestions}
                                label="Suggest follow-up questions"
                                onChange={onSuggestFollowupQuestionsChange}
                            />
                        </StackItem>
                        <StackItem grow align="start" styles={{ root: { width: "50%" } }}>
                            <Label className={styles.panelSubHeaderText}>Document Settings</Label>
                            {isAdvancedModeEnabled ? (
                                <Dropdown
                                    id="retrievalMode"
                                    className={styles.chatSettingsSeparator}
                                    label="Document search mode"
                                    options={retrievalModeOptions}
                                    onChange={(_event, option) => onRetrievalModeChange(option?.key as RetrievalMode)}
                                    selectedKey={retrievalMode}
                                />
                            ) : (
                                <Toggle
                                    label="Document search mode"
                                    onText="On"
                                    offText="Off"
                                    checked={retrievalMode !== RetrievalMode.None}
                                    onChange={handleRagClick}
                                />
                            )}
                            {retrievalMode !== RetrievalMode.None &&
                                <>
                                    {isAdvancedModeEnabled && (
                                        <SpinButton
                                            id="retrieveCount"
                                            className={styles.chatSettingsSeparator}
                                            label="Documents sections to retrieve"
                                            min={1}
                                            max={20}
                                            defaultValue={retrieveCount.toString()}
                                            onChange={onRetrieveCountChange}
                                        />
                                    )}
                                    <FolderPicker
                                        className={styles.chatSettingsSeparator}
                                        allowFolderCreation={false}
                                        onSelectedKeyChanged={onSelectedKeyChanged}
                                        selectableFolders={selectableFolders}
                                        selectedKeys={selectedFolders}
                                        userData={userData}
                                    />
                                    <TagPickerInline
                                        className={styles.chatSettingsSeparator}
                                        allowNewTags={false}
                                        onSelectedTagsChange={onSelectedTagsChange}
                                        preSelectedTags={selectedTags}
                                        useDarkMode={userContext.useDarkMode}
                                    />
                                </>
                            }
                        </StackItem>
                    </Stack>
                    {isAdvancedModeEnabled && (
                        <PromptOverride
                            className={styles.chatSettingsSeparator}
                            value={promptOverride}
                            onChange={onPromptOverrideChange}
                        />
                    )}
                    <Dialog
                        hidden={!isTemplateSaveDialogOpen}
                        onDismiss={() => setIsTemplateSaveDialogOpen(false)}
                        dialogContentProps={{
                            type: DialogType.largeHeader,
                            title: "Create Prompt Template"
                        }}
                        modalProps={{
                            isBlocking: true,
                            styles: {
                                main: {
                                    maxWidth: "750px !important",
                                    minWidth: "500px !important",
                                },
                            },
                        }}>
                        <p>
                            Enter a name for your template.<br />
                            Note: This will overwrite any other template with the same name.
                        </p>
                        <TextField
                            value={templateSaveName}
                            disabled={isTemplateSaving}
                            onChange={(e, newValue) => setTemplateSaveName(newValue || "")} />
                        <DialogFooter>
                            <PrimaryButton
                                iconProps={{ iconName: "Save" }}
                                className={styles.panelButton}
                                disabled={isTemplateSaving || templateSaveName.length == 0}
                                onClick={onSavePromptTemplate}
                                text="Save"
                            />
                            <DefaultButton
                                iconProps={{ iconName: "Cancel" }}
                                className={styles.panelButton}
                                disabled={isTemplateSaving}
                                onClick={() => setIsTemplateSaveDialogOpen(false)}
                                text="Cancel"
                            />
                        </DialogFooter>
                    </Dialog>
                </Panel>
                <Tooltips />
            </div>
        </div>
    );
};

export default Chat;